// Strings of the tobacco municipality fees (migration 049, request of 30 Oct 2026, item 1; docs/tobacco-fees.md).
// The defaults (100 % and SAR 25.00) are the business defaults supplied to Delta, never described as verified legal
// rates. Nothing here claims ZATCA Phase 2 support. Arabic needs review by an Arabic copy editor.
export const TOBACCO_STRINGS: Record<"en" | "ar", Record<string, string>> = {
  en: {
    tobSettings: "Tobacco fees",
    tobStatus: "Tobacco municipality fees",
    tobIntro: "When switched on, sales of items marked “Subject to tobacco fees” charge the municipal fee, shown apart from VAT and owed to the municipality, never counted as sales revenue. It is off for every business until you switch it on.",
    tobEnable: "Charge tobacco fees on new sales",
    tobOnHint: "Switching it off stops the fee on new sales only. Issued invoices, refunds, the report and the liability balance stay as they are.",
    tobOffHint: "Off. To switch it on, choose the liability account and decide the three open cases below.",
    tobOn: "On",
    tobOff: "Off",
    tobItems: "Items subject to the fee: {n}",
    tobItemsLink: "Items",
    tobUpdated: "Last changed by {u}, {at}",
    tobLicence: "Licence",
    tobLicenceHint: "Kept exactly as entered: digits only, leading zeros kept.",
    tobLicenceNo: "Licence number",
    tobLicenceNoHint: "Digits only (for example 000123450).",
    tobMunicipality: "Municipality",
    tobExpires: "Licence expiry date",
    tobCalc: "Calculation and account",
    tobCalcHint: "The fee is worked out on the product amount after discounts. VAT is then charged on the product amount plus the fee.",
    tobPct: "Fee percentage",
    tobMinimum: "Minimum fee",
    tobDefaultsNote: "100 % and SAR 25.00 are the business defaults supplied to Delta. They are not independently verified legal rates: check them against your licence before switching the fee on.",
    tobPriceIncl: "Entered prices include the tobacco fee",
    tobPriceInclHint: "Separate from “price includes VAT”, which each item keeps. When on, the price is split into product and fee.",
    tobAccount: "Liability account for the fees",
    tobAccountNone: "– Not chosen –",
    tobAccountHint: "Only posting accounts of your own chart classified under Liabilities. Delta never creates or guesses one.",
    tobDecisions: "Fee policies",
    tobDecisionsHint: "Choose each policy for this business before switching the fee on; nothing is preselected. Every invoice keeps the policies it was issued under, so a later change never alters it. Examples: 15 % VAT, fee 100 %, minimum SAR 25.00, a carton holding 10 packs (the pack is the base unit).",
    tobDecA: "Minimum fee basis",
    tobA1: "Per base unit (each pack, also inside a carton)",
    tobA1Ex: "1 carton (selling qty 1, base qty 10 packs) at 200.00: product 200.00; fee 250.00 (minimum 10 × 25.00, above 100 % = 200.00); VAT 67.50; total 517.50. Half a carton (base qty 5): product 100.00; fee 125.00; VAT 33.75; total 258.75.",
    tobA2: "Per selling unit (each carton counts once)",
    tobA2Ex: "1 carton (selling qty 1, base qty 10 packs) at 200.00: product 200.00; fee 200.00 (100 %, above the minimum 1 × 25.00); VAT 60.00; total 460.00. Half a carton (selling qty 0.5): product 100.00; fee 100.00; VAT 30.00; total 230.00.",
    tobDecB: "Fee-inclusive price below the minimum (only when prices include the fee)",
    tobB1: "Reject the price",
    tobB1Ex: "A pack entered at 20.00 including the fee cannot hold a 25.00 fee: the line is refused until the price is raised.",
    tobB2: "Add the minimum fee on top, with a visible revised total",
    tobB2Ex: "A pack entered at 20.00 (23.00 with VAT): product 20.00 + fee 25.00 + VAT 6.75 = 51.75. The screen shows 23.00 → 51.75 and the user must confirm the revised total before anything is saved.",
    tobDecC: "Invoice discount",
    tobC1: "Distribute across all eligible product lines",
    tobC1Ex: "60.00 tobacco + 40.00 other, 10.00 off: 6.00 and 4.00 by value; tobacco product 54.00, fee 54.00; total 165.60.",
    tobC2: "Exclude tobacco-subject lines",
    tobC2Ex: "Same sale: all 10.00 comes off the other line; tobacco product 60.00, fee 60.00; total 172.50. Refused when every line is subject to the fee.",
    tobUndecided: "Not decided",
    tobReason: "Reason for the change",
    tobReasonHint: "Recorded in the activity log with the values before and after.",
    tobNoZatcaClaim: "The fee is shown on invoices, receipts and in the e-invoice XML as a separate charge. This is not ZATCA Phase 2 support and has not been validated or accepted by ZATCA.",
    tobLocked: "Only the business owner or a user with the advanced accounting right (journalEntryAdd) can change tobacco fee settings.",
    tobDigitsOnly: "Digits only.",
    tobEnableNeeds: "To switch it on, choose: {x}.",
    tobSavedOn: "Tobacco fees saved: on for new sales.",
    tobSavedOff: "Tobacco fee settings saved: off.",
    tobItemFlag: "Subject to tobacco fees",
    tobItemHint: "Sales of this item charge the municipal tobacco fee while it is switched on in Settings › Tobacco fees.",
    tobFee: "Tobacco municipal fee",
    tobFeeShort: "Tobacco fee {x}",
    tobLineFee: "Tobacco fee {x}",
    tobLineFeeMin: "Tobacco fee {x} (minimum)",
    tobShortfall: "This price includes the tobacco fee but cannot cover the minimum fee. Raise the price.",
    tobRaisedRow: "Minimum fee added on top: {a} at the entered prices, {b} now",
    tobRaiseTitle: "Confirm the revised total",
    tobRaiseSub: "These prices include the tobacco fee but cannot cover the minimum fee. Your business adds the minimum on top, which raises the customer's total. Nothing is saved until you confirm.",
    tobRaiseEntered: "Total at the entered prices",
    tobRaiseRevised: "Revised total",
    tobRaiseBy: "Raised by",
    tobRaiseLine: "Line {n}: {a} → {b} (tobacco fee {f})",
    tobRaiseGo: "Confirm {x}",
    tobRaisedNote: "Total raised by {x}: the minimum tobacco fee was added on top of fee-inclusive prices, as confirmed when the sale was made.",
    errTobaccoConfirm: "The minimum tobacco fee raises this total. Confirm the revised total to continue.",
    errTobaccoAccount: "The tobacco fee account this invoice was issued with is no longer a usable liability account. Delta does not substitute another account: classify it under Liabilities again, then try again.",
    tobEinvNote: "Tobacco municipal fee {x}: a separate line charge in the XML, apart from VAT. Not validated with ZATCA.",
    tobReport: "Tobacco fees",
    tobReportSheet: "Tobacco fees",
    tobKindSale: "Sale",
    tobKindRefund: "Refund",
    tobBase: "Product amount",
    tobCollected: "Collected",
    tobRefunded: "Refunded",
    tobNet: "Net",
    tobBalanceOf: "Balance of {a}",
    tobBalanceNote: "as posted to the end of the period (all sources).",
    tobXlsx: "Excel (.xlsx)",
    tobPdf: "PDF",
    errTobaccoNotAllowed: "Only the business owner or a user with the advanced accounting right can change tobacco fee settings.",
    fieldDigitsOnly: "Digits only.",
    fieldNotLiability: "Choose an account classified under Liabilities.",
    fieldRequiredToEnable: "Required to switch the fee on.",
    fieldDecisionRequired: "Choose one before switching the fee on.",
    fieldTobDiscountNoBase: "Every line is subject to the tobacco fee and the discount is kept off them: there is nothing to discount."
  },
  ar: {
    tobSettings: "رسوم التبغ",
    tobStatus: "رسوم البلدية على التبغ",
    tobIntro: "عند التفعيل تُحتسب رسوم البلدية على مبيعات الأصناف المعلَّمة «خاضع لرسوم التبغ»، وتظهر منفصلة عن ضريبة القيمة المضافة كالتزام مستحق للبلدية، ولا تُحسب إيرادَ مبيعات. الميزة متوقفة لكل منشأة حتى تفعّلها.",
    tobEnable: "احتساب رسوم التبغ على المبيعات الجديدة",
    tobOnHint: "الإيقاف يمنع الرسوم على المبيعات الجديدة فقط. تبقى الفواتير الصادرة والمرتجعات والتقرير ورصيد الالتزام كما هي.",
    tobOffHint: "متوقفة. للتفعيل اختر حساب الالتزام وحدّد الحالات الثلاث المفتوحة أدناه.",
    tobOn: "مفعّلة",
    tobOff: "متوقفة",
    tobItems: "الأصناف الخاضعة للرسوم: {n}",
    tobItemsLink: "الأصناف",
    tobUpdated: "آخر تعديل بواسطة {u}، {at}",
    tobLicence: "الترخيص",
    tobLicenceHint: "يُحفظ كما أُدخل تمامًا: أرقام فقط مع الإبقاء على الأصفار البادئة.",
    tobLicenceNo: "رقم الترخيص",
    tobLicenceNoHint: "أرقام فقط (مثال 000123450).",
    tobMunicipality: "البلدية",
    tobExpires: "تاريخ انتهاء الترخيص",
    tobCalc: "الاحتساب والحساب",
    tobCalcHint: "تُحتسب الرسوم على مبلغ المنتج بعد الخصومات، ثم تُحتسب ضريبة القيمة المضافة على مبلغ المنتج مضافًا إليه الرسوم.",
    tobPct: "نسبة الرسوم",
    tobMinimum: "الحد الأدنى للرسوم",
    tobDefaultsNote: "‏100٪ و25.00 ريال هي القيم الافتراضية التي زُوِّدت بها دلتا من النشاط التجاري، وليست أسعارًا نظامية تم التحقق منها بشكل مستقل. راجعها مقابل ترخيصك قبل التفعيل.",
    tobPriceIncl: "الأسعار المُدخلة تشمل رسوم التبغ",
    tobPriceInclHint: "منفصل عن «السعر يشمل الضريبة» الخاص بكل صنف. عند التفعيل يُقسَّم السعر إلى منتج ورسوم.",
    tobAccount: "حساب الالتزام للرسوم",
    tobAccountNone: "– لم يُختر –",
    tobAccountHint: "حسابات الترحيل في دليل حساباتك المصنفة ضمن الخصوم فقط. لا تُنشئ دلتا حسابًا ولا تخمّنه.",
    tobDecisions: "سياسات الرسوم",
    tobDecisionsHint: "اختر كل سياسة لهذه المنشأة قبل تفعيل الرسوم؛ لا يوجد اختيار مسبق. كل فاتورة تحتفظ بالسياسات التي صدرت بها، فلا يغيّرها أي تعديل لاحق. الأمثلة: ضريبة 15٪، رسوم 100٪، حد أدنى 25.00 ريال، كرتون فيه 10 علب (العلبة هي الوحدة الأساسية).",
    tobDecA: "أساس الحد الأدنى للرسوم",
    tobA1: "لكل وحدة أساسية (كل علبة، حتى داخل الكرتون)",
    tobA1Ex: "كرتون واحد (كمية البيع 1، الكمية الأساسية 10 علب) بسعر 200.00: المنتج 200.00؛ الرسوم 250.00 (الحد الأدنى 10 × 25.00، أعلى من 100٪ = 200.00)؛ الضريبة 67.50؛ الإجمالي 517.50. نصف كرتون (الكمية الأساسية 5): المنتج 100.00؛ الرسوم 125.00؛ الضريبة 33.75؛ الإجمالي 258.75.",
    tobA2: "لكل وحدة بيع (الكرتون يُحسب مرة واحدة)",
    tobA2Ex: "كرتون واحد (كمية البيع 1، الكمية الأساسية 10 علب) بسعر 200.00: المنتج 200.00؛ الرسوم 200.00 (100٪، أعلى من الحد الأدنى 1 × 25.00)؛ الضريبة 60.00؛ الإجمالي 460.00. نصف كرتون (كمية البيع 0.5): المنتج 100.00؛ الرسوم 100.00؛ الضريبة 30.00؛ الإجمالي 230.00.",
    tobDecB: "سعر شامل للرسوم أقل من الحد الأدنى (فقط إذا كانت الأسعار شاملة للرسوم)",
    tobB1: "رفض السعر",
    tobB1Ex: "علبة مُدخلة بسعر 20.00 شاملًا الرسوم لا تتسع لرسوم 25.00: يُرفض السطر حتى يُرفع السعر.",
    tobB2: "إضافة الحد الأدنى فوق السعر مع إظهار الإجمالي المعدَّل",
    tobB2Ex: "علبة مُدخلة بسعر 20.00 (23.00 مع الضريبة): المنتج 20.00 + الرسوم 25.00 + الضريبة 6.75 = 51.75. تعرض الشاشة 23.00 ← 51.75 ويجب أن يؤكد المستخدم الإجمالي المعدَّل قبل الحفظ.",
    tobDecC: "خصم الفاتورة",
    tobC1: "يوزَّع على كل أسطر المنتجات المؤهلة",
    tobC1Ex: "تبغ 60.00 وصنف آخر 40.00 وخصم 10.00: ‏6.00 و4.00 حسب القيمة؛ منتج التبغ 54.00 والرسوم 54.00؛ الإجمالي 165.60.",
    tobC2: "تُستثنى أسطر المنتجات الخاضعة للرسوم",
    tobC2Ex: "البيع نفسه: يُخصم 10.00 كاملًا من السطر الآخر؛ منتج التبغ 60.00 والرسوم 60.00؛ الإجمالي 172.50. يُرفض إذا كانت كل الأسطر خاضعة للرسوم.",
    tobUndecided: "لم يُحدَّد",
    tobReason: "سبب التعديل",
    tobReasonHint: "يُسجَّل في سجل النشاط مع القيم قبل التعديل وبعده.",
    tobNoZatcaClaim: "تظهر الرسوم في الفواتير والإيصالات وفي ملف XML للفاتورة الإلكترونية كرسم منفصل. هذا ليس دعمًا للمرحلة الثانية من هيئة الزكاة والضريبة والجمارك ولم يُتحقق منه أو يُعتمد من الهيئة.",
    tobLocked: "تعديل إعدادات رسوم التبغ لمالك المنشأة أو لمستخدم لديه صلاحية المحاسبة المتقدمة (journalEntryAdd) فقط.",
    tobDigitsOnly: "أرقام فقط.",
    tobEnableNeeds: "للتفعيل اختر: {x}.",
    tobSavedOn: "حُفظت رسوم التبغ: مفعّلة على المبيعات الجديدة.",
    tobSavedOff: "حُفظت إعدادات رسوم التبغ: متوقفة.",
    tobItemFlag: "خاضع لرسوم التبغ",
    tobItemHint: "تُحتسب رسوم البلدية على التبغ عند بيع هذا الصنف ما دامت مفعّلة في الإعدادات › رسوم التبغ.",
    tobFee: "رسوم البلدية على التبغ",
    tobFeeShort: "رسوم التبغ {x}",
    tobLineFee: "رسوم التبغ {x}",
    tobLineFeeMin: "رسوم التبغ {x} (الحد الأدنى)",
    tobShortfall: "هذا السعر يشمل رسوم التبغ لكنه لا يتسع للحد الأدنى. ارفع السعر.",
    tobRaisedRow: "أُضيف الحد الأدنى للرسوم فوق السعر: {a} بالأسعار المدخلة، و{b} الآن",
    tobRaiseTitle: "أكّد الإجمالي المعدَّل",
    tobRaiseSub: "هذه الأسعار تشمل رسوم التبغ لكنها لا تتسع للحد الأدنى للرسوم. منشأتك تضيف الحد الأدنى فوق السعر، فيرتفع إجمالي العميل. لا يُحفظ شيء حتى تؤكّد.",
    tobRaiseEntered: "الإجمالي بالأسعار المدخلة",
    tobRaiseRevised: "الإجمالي المعدَّل",
    tobRaiseBy: "مقدار الزيادة",
    tobRaiseLine: "السطر {n}: {a} ← {b} (رسوم التبغ {f})",
    tobRaiseGo: "تأكيد {x}",
    tobRaisedNote: "ارتفع الإجمالي بمقدار {x}: أُضيف الحد الأدنى لرسوم التبغ فوق أسعار تشمل الرسوم، كما أُكّد عند البيع.",
    errTobaccoConfirm: "الحد الأدنى لرسوم التبغ يرفع هذا الإجمالي. أكّد الإجمالي المعدَّل للمتابعة.",
    errTobaccoAccount: "حساب رسوم التبغ الذي صدرت به هذه الفاتورة لم يعد حساب التزامات صالحًا. لا يستبدله Delta بحساب آخر: أعد تصنيفه ضمن الالتزامات ثم حاول مجددًا.",
    tobEinvNote: "رسوم البلدية على التبغ {x}: رسم منفصل على السطر في ملف XML، مستقل عن الضريبة. لم يُتحقق منه مع الهيئة.",
    tobReport: "رسوم التبغ",
    tobReportSheet: "رسوم التبغ",
    tobKindSale: "بيع",
    tobKindRefund: "مرتجع",
    tobBase: "مبلغ المنتج",
    tobCollected: "المحصَّل",
    tobRefunded: "المردود",
    tobNet: "الصافي",
    tobBalanceOf: "رصيد {a}",
    tobBalanceNote: "كما هو مُرحَّل حتى نهاية الفترة (كل المصادر).",
    tobXlsx: "Excel ‏(.xlsx)",
    tobPdf: "PDF",
    errTobaccoNotAllowed: "تعديل إعدادات رسوم التبغ لمالك المنشأة أو لمستخدم لديه صلاحية المحاسبة المتقدمة فقط.",
    fieldDigitsOnly: "أرقام فقط.",
    fieldNotLiability: "اختر حسابًا مصنفًا ضمن الخصوم.",
    fieldRequiredToEnable: "مطلوب لتفعيل الرسوم.",
    fieldDecisionRequired: "اختر واحدًا قبل تفعيل الرسوم.",
    fieldTobDiscountNoBase: "كل الأسطر خاضعة لرسوم التبغ والخصم لا يُطبَّق عليها: لا يوجد ما يُخصم منه."
  }
};
